import { prepareAmbientLoop, type NoiseType } from './ambientLoop';
type Voice={source:AudioBufferSourceNode;gain:GainNode};
/** One user-started player. Decoded audio stays in memory, never in draft storage. */
export class AmbientPlayer {
  private context:AudioContext|null=null;
  private master:GainNode|null=null;
  private voices=new Set<Voice>();
  private timer:ReturnType<typeof setInterval>|null=null;
  private request=0;
  private cache=new Map<NoiseType,{buffer:AudioBuffer;loop:{start:number;end:number}}>();
  private volume=.5;
  constructor(private load:(type:NoiseType)=>Promise<ArrayBuffer>) {}
  setVolume(value:number) {
    this.volume=Math.max(0,Math.min(1,value));
    if(this.context&&this.master) this.master.gain.setTargetAtTime(this.volume,this.context.currentTime,.04);
  }
  async play(type:NoiseType):Promise<boolean> {
    this.stop();const request=this.request;
    if(!this.context) {this.context=new AudioContext();this.master=this.context.createGain();this.master.connect(this.context.destination);}
    const context=this.context,master=this.master!;
    // Resume directly from the click, before the download, for mobile autoplay rules.
    await context.resume();
    if(request!==this.request) return false;
    let entry=this.cache.get(type);
    if(entry) {this.cache.delete(type);this.cache.set(type,entry);}
    if(!entry) {
      const raw=await this.load(type);
      if(request!==this.request) return false;
      const buffer=await context.decodeAudioData(raw);
      if(request!==this.request) return false;
      entry={buffer,loop:prepareAmbientLoop(buffer,type)};
      // Limit decoded memory to the two most recently used tracks.
      if(this.cache.size>=2) this.cache.delete(this.cache.keys().next().value!);
      this.cache.set(type,entry);
    }
    const {buffer,loop}=entry, duration=loop.end-loop.start;
    const crossfade=Math.min(.8,type==='underwater'?.65:type==='brown'?.24:.45,duration*(type==='underwater'?.42:type==='brown'?.34:.08));
    const step=Math.max(.25,duration-crossfade);
    const fadeIn=Float32Array.from({length:256},(_,i)=>Math.sin(i/255*Math.PI/2));
    const fadeOut=Float32Array.from({length:256},(_,i)=>Math.cos(i/255*Math.PI/2));
    const voice=(time:number,first=false)=>{
      const source=context.createBufferSource(),gain=context.createGain();source.buffer=buffer;
      source.connect(gain);gain.connect(master);gain.gain.setValueAtTime(first?1:0,time);
      const item={source,gain};this.voices.add(item);
      source.onended=()=>{source.disconnect();gain.disconnect();this.voices.delete(item);};
      source.start(time,loop.start,Math.min(duration+crossfade+.08,buffer.duration-loop.start));
      return item;
    };
    const start=context.currentTime+.06;
    let current=voice(start,true), next=start+step;
    master.gain.cancelScheduledValues(context.currentTime);master.gain.setValueAtTime(0,context.currentTime);
    master.gain.linearRampToValueAtTime(this.volume,start+.45);
    const schedule=()=>{
      if(request!==this.request||context.state!=='running') return;
      // Several future voices also cover background-tab timer throttling.
      if(next<context.currentTime) next=context.currentTime+.02;
      while(next<context.currentTime+Math.max(8,step*2)) {
        const following=voice(next);
        following.gain.gain.setValueCurveAtTime(fadeIn,next,crossfade);
        current.gain.gain.cancelScheduledValues(next);current.gain.gain.setValueAtTime(1,next);
        current.gain.gain.setValueCurveAtTime(fadeOut,next,crossfade);
        try {current.source.stop(next+crossfade+.06);} catch { /* Already ended. */ }
        current=following;next+=step;
      }
    };
    schedule();this.timer=setInterval(schedule,40);return true;
  }
  stop() {
    this.request++;
    if(this.timer!==null) {clearInterval(this.timer);this.timer=null;}
    const now=this.context?.currentTime??0;
    if(this.master) {this.master.gain.cancelScheduledValues(now);this.master.gain.setTargetAtTime(0,now,.025);}
    for(const voice of this.voices) {try {voice.source.stop(now+.12);} catch { /* Already ended. */ }}
  }
  dispose() {this.stop();this.cache.clear();if(this.context) void this.context.close();this.context=null;this.master=null;}
}
